const API_BASE_URL =
  process.env.REACT_APP_API_SERVER_URL || "http://localhost:8000";

export const MFA_BACKUP_LOW_BANNER_KEY = "handbook_mfa_backup_low_banner";

let memoryToken = null;
let sessionUser = null;
let restorePromise = null;
let fetchPatched = false;
const tokenListeners = new Set();

function notifyTokenListeners() {
  tokenListeners.forEach((listener) => {
    try {
      listener(memoryToken);
    } catch {
      /* ignore */
    }
  });
}

function userFromJwt(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.mfa_pending) return null;
    return {
      id: payload.user_id,
      email: payload.email,
      name: payload.name,
      provider: payload.provider,
      role: payload.role || "Employee",
      mfa_enabled: payload.mfa_enabled === true,
      exp: payload.exp,
    };
  } catch {
    return null;
  }
}

function userFromProfile(user) {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    provider: user.provider,
    role: user.role || "Employee",
    mfa_enabled: user.mfa_enabled === true,
  };
}

function clearLegacyTokenStorage() {
  try {
    localStorage.removeItem("oauth_token");
  } catch {
    /* ignore */
  }
}

function isApiUrl(url) {
  if (!url) return false;
  const value = String(url);
  return value.includes("/api/") || value.startsWith(API_BASE_URL);
}

async function refreshMemoryToken() {
  try {
    const res = await fetch(`${API_BASE_URL}/api/oauth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) return false;
    const data = await res.json().catch(() => ({}));
    if (!data.token) return false;
    memoryToken = data.token;
    sessionUser = userFromJwt(data.token) || sessionUser;
    notifyTokenListeners();
    return true;
  } catch {
    return false;
  }
}

export const oauthService = {
  loginWithGoogle: () => {
    window.location.href = `${API_BASE_URL}/api/oauth/google/login`;
  },

  setToken: (token) => {
    clearLegacyTokenStorage();
    memoryToken = token || null;
    sessionUser = token ? userFromJwt(token) : null;
    notifyTokenListeners();
  },

  setSessionUser: (user) => {
    sessionUser = userFromProfile(user);
  },

  getToken: () => memoryToken,

  subscribeToken: (listener) => {
    tokenListeners.add(listener);
    return () => tokenListeners.delete(listener);
  },

  removeToken: () => {
    memoryToken = null;
    sessionUser = null;
    clearLegacyTokenStorage();
    notifyTokenListeners();
  },

  isAuthenticated: () => {
    if (sessionUser) {
      if (sessionUser.exp && sessionUser.exp <= Date.now() / 1000) {
        return false;
      }
      return true;
    }
    const token = memoryToken;
    if (!token) return false;
    const user = userFromJwt(token);
    return Boolean(user);
  },

  getUserInfo: () => {
    if (sessionUser) return sessionUser;
    const token = memoryToken;
    if (!token) return null;
    return userFromJwt(token);
  },

  restoreSession: () => {
    if (oauthService.isAuthenticated() && memoryToken) {
      return Promise.resolve(true);
    }
    if (restorePromise) return restorePromise;

    const finishRestore = async () => {
      if (oauthService.isAuthenticated()) {
        return refreshMemoryToken();
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/oauth/profile`, {
          credentials: "include",
        });
        if (!res.ok) return false;
        const data = await res.json().catch(() => ({}));
        const user = userFromProfile(data.user);
        if (!user) return false;
        sessionUser = user;
        return refreshMemoryToken();
      } catch {
        return false;
      }
    };

    restorePromise = finishRestore().finally(() => {
      restorePromise = null;
    });
    return restorePromise;
  },

  exchangeHandoffCode: async (code) => {
    const res = await fetch(`${API_BASE_URL}/api/oauth/exchange`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: String(code ?? "").trim() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.message || "Sign-in failed");
      err.status = res.status;
      throw err;
    }
    if (!data.token) {
      throw new Error("No authentication token received");
    }
    return {
      token: data.token,
      mfa_pending: Boolean(data.mfa_pending),
    };
  },

  completeMfaLogin: async (pendingToken, code) => {
    const headers = { "Content-Type": "application/json" };
    if (pendingToken) headers.Authorization = `Bearer ${pendingToken}`;
    const res = await fetch(`${API_BASE_URL}/api/oauth/mfa/complete-login`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ code: String(code ?? "").trim() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.message || "Sign-in failed");
      err.status = res.status;
      if (data.code) err.code = data.code;
      throw err;
    }
    if (data.mfa_backup_codes_low) {
      try {
        sessionStorage.setItem(
          MFA_BACKUP_LOW_BANNER_KEY,
          String(Number(data.mfa_backup_codes_remaining) || 0)
        );
      } catch {
        /* ignore */
      }
    }
    if (data.user) {
      sessionUser = userFromProfile(data.user);
    }
    if (data.token) {
      memoryToken = data.token;
      sessionUser = userFromJwt(data.token) || sessionUser;
      notifyTokenListeners();
    }
    return { token: data.token, user: data.user };
  },

  logout: async () => {
    try {
      const headers = { "Content-Type": "application/json" };
      if (memoryToken) headers.Authorization = `Bearer ${memoryToken}`;
      await fetch(`${API_BASE_URL}/api/oauth/logout`, {
        method: "POST",
        credentials: "include",
        headers,
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      try {
        sessionStorage.removeItem(MFA_BACKUP_LOW_BANNER_KEY);
      } catch {
        /* ignore */
      }
      oauthService.removeToken();
      window.location.href = "/login";
    }
  },

  installAuthFetch: () => {
    if (fetchPatched || typeof window === "undefined" || typeof window.fetch !== "function") {
      return;
    }
    fetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      const url = typeof input === "string" ? input : input?.url;
      if (!isApiUrl(url)) {
        return originalFetch(input, init);
      }
      const nextInit = { ...init, credentials: init.credentials || "include" };
      const headers = new Headers(init.headers || {});
      const auth = headers.get("Authorization");
      if (auth && /^Bearer\s+(null|undefined)?$/i.test(auth.trim())) {
        headers.delete("Authorization");
      }
      if (!headers.has("Authorization") && memoryToken) {
        headers.set("Authorization", `Bearer ${memoryToken}`);
      }
      nextInit.headers = headers;
      return originalFetch(input, nextInit);
    };
  },
};

oauthService.installAuthFetch();
clearLegacyTokenStorage();
